import React, { useMemo } from 'react';
import { Card } from 'primereact/card';
import { useTranslation } from 'react-i18next';
import { RTUData, PortForwardEntry, PortForwardSnapshot } from '../../types/RtuData';
import { StaticCompositeTable } from './StaticCompositeTable';
import { RtuStaticModuleCardTitle } from './RtuStaticModuleCardTitle';

interface PortForwardRow {
  port: string;
  status: string;
}

/**
 * Derives rows from a PortForwardSnapshot.
 *
 * Filter rule: skip entries where both port is "0" (or blank) AND status is
 * "Disabled" — these are unconfigured placeholder slots that the backend
 * emits for padding. All other entries (including explicitly configured
 * disabled entries on real port numbers) are preserved.
 */
function deriveRows(snapshot: PortForwardSnapshot | undefined): PortForwardRow[] {
  if (!snapshot?.entries) return [];

  const rows: PortForwardRow[] = [];

  for (const entry of Object.values(snapshot.entries) as PortForwardEntry[]) {
    const portStr  = entry.portNo ?? '0';
    const statusStr = entry.status ?? '—';

    const isPlaceholder =
      (portStr === '0' || portStr.trim() === '') && statusStr === 'Disabled';

    if (!isPlaceholder) {
      rows.push({ port: portStr, status: statusStr });
    }
  }

  // If every entry was a placeholder, surface a single summary row so the card
  // body is never blank.
  if (rows.length === 0 && Object.keys(snapshot.entries).length > 0) {
    rows.push({ port: '—', status: 'All Disabled' });
  }

  return rows;
}

interface Props {
  data: RTUData | null | undefined;
}

/**
 * RtuPortForwardCard
 *
 * Displays the Port_forward composite block from RTU static data as a
 * structured table (Port / Status).  Placeholder disabled-zero entries are
 * suppressed; a config meta row is shown above the table.
 */
export const RtuPortForwardCard = ({ data }: Props) => {
  const { t } = useTranslation();

  const rows    = useMemo(() => deriveRows(data?.rtuStaticData?.portForward), [data]);
  const config  = data?.rtuStaticData?.portForward?.config ?? '—';

  return (
    <Card
      title={
        <RtuStaticModuleCardTitle
          title={t('rtuDetail.staticData.portForward.title', { defaultValue: 'Port Forwarding' })}
          status={config}
        />
      }
      className="compact-card flat-card rtu-static-module-card"
    >
      <StaticCompositeTable
        rows={rows}
        columns={[
          { field: 'port',   header: t('rtuDetail.staticData.portForward.colPort',   { defaultValue: 'Port'   }), width: '6rem' },
          {
            field: 'status',
            header: t('rtuDetail.staticData.portForward.colStatus', { defaultValue: 'Status' }),
            statusTone: true,
            statusIconTooltips: {
              positive: 'rtuDetail.staticData.portForward.statusIcon.positive',
              negative: 'rtuDetail.staticData.portForward.statusIcon.negative',
            },
          },
        ]}
        emptyMessage={t('table.noRowsFound', { defaultValue: 'No port forward entries.' })}
      />
    </Card>
  );
};

export default RtuPortForwardCard;
